<template>
  <div
    class="link-bio-page min-h-screen w-full"
    :class="[`theme-${theme}`]"
    :style="pageStyle"
  >
    <div class="link-bio-atmosphere" aria-hidden="true" />
    <div class="link-bio-shell">
      <header class="link-bio-profile ps-animate-scale" :style="{ color: page.text_color }">
        <div class="link-bio-avatar-wrap">
          <img
            v-if="page.profile_picture"
            :src="page.profile_picture"
            :alt="page.name || 'Profile'"
            class="link-bio-avatar"
            :style="{ boxShadow: `0 0 0 3px ${page.button_color}33` }"
            @error="$event.target.style.display = 'none'"
          />
          <div v-else class="link-bio-avatar link-bio-avatar--fallback">
            <icon name="user" class="w-14 h-14 sm:w-16 sm:h-16 opacity-40" />
          </div>
        </div>

        <h1 v-if="page.name" class="link-bio-name">{{ page.name }}</h1>
        <p v-if="page.title" class="link-bio-role">{{ page.title }}</p>
        <p v-if="page.bio" class="link-bio-bio">{{ page.bio }}</p>
      </header>

      <nav class="link-bio-links" aria-label="Profile links">
        <a
          v-for="(link, index) in pageLinks"
          :key="index"
          :href="formatUrl(link.url)"
          target="_blank"
          rel="noopener noreferrer"
          class="link-bio-btn ps-animate-in group"
          :class="buttonClass"
          :style="{
            '--btn-bg': page.button_color,
            animationDelay: `${0.08 + index * 0.05}s`,
          }"
        >
          <span class="link-bio-btn__icon" aria-hidden="true">
            <icon :name="getIcon(link.url)" class="w-4 h-4" />
          </span>
          <span class="link-bio-btn__label">{{ link.title || `Link ${index + 1}` }}</span>
          <icon name="arrow-right" class="link-bio-btn__arrow w-4 h-4" />
        </a>

        <div
          v-if="pageLinks.length === 0"
          class="link-bio-empty"
          :style="{ borderColor: page.text_color + '30', color: page.text_color }"
        >
          <icon name="website" class="w-8 h-8 opacity-40 mx-auto mb-3" />
          <p class="text-sm font-medium opacity-70">No links available</p>
        </div>
      </nav>

      <div v-if="enableLeadCapture" class="link-bio-lead ps-animate-in" style="animation-delay: 0.35s">
        <ContactForm
          :profile-id="page.id"
          :form-type="leadFormType"
          :button-color="page.button_color"
          :text-color="page.text_color"
          :border-color="page.text_color + '20'"
        />
      </div>

      <footer class="link-bio-footer" :style="{ color: page.text_color }">
        <p>ProfileSuite</p>
      </footer>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import ContactForm from '@/Shared/ContactForm.vue'
import { getLinkIcon } from '@/utils/linkIcons.js'

export default {
  components: {
    Icon,
    ContactForm,
  },
  props: {
    page: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Link in Bio',
    },
    enableLeadCapture: {
      type: Boolean,
      default: false,
    },
    leadFormType: {
      type: String,
      default: 'email_only',
    },
  },
  computed: {
    theme() {
      return this.page.theme || 'classic'
    },
    pageLinks() {
      if (!this.page.links) return []
      if (typeof this.page.links === 'string') {
        try {
          return JSON.parse(this.page.links)
        } catch (e) {
          return []
        }
      }
      return this.page.links
    },
    pageStyle() {
      return {
        '--bio-bg': this.page.background_color || '#f4f7f5',
        '--bio-text': this.page.text_color || '#0f172a',
        '--bio-accent': this.page.button_color || '#0f766e',
        backgroundColor: this.page.background_color,
        backgroundImage: this.page.background_image
          ? `url(${this.page.background_image})`
          : undefined,
      }
    },
    buttonClass() {
      switch (this.page.button_style) {
        case 'square':
          return 'is-square'
        case 'pill':
          return 'is-pill'
        case 'rounded':
        default:
          return 'is-rounded'
      }
    },
  },
  methods: {
    formatUrl(url) {
      if (!url) return '#'
      if (url.indexOf('://') === -1 && !url.startsWith('mailto:') && !url.startsWith('tel:')) {
        return 'https://' + url
      }
      return url
    },
    getIcon(url) {
      return getLinkIcon(url)
    },
  },
}
</script>

<style scoped>
.link-bio-page {
  --bio-bg: #f4f7f5;
  --bio-text: #0f172a;
  --bio-accent: #0f766e;
  position: relative;
  font-family: var(--ps-font-body);
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: var(--bio-text);
}

.link-bio-atmosphere {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, color-mix(in srgb, var(--bio-accent) 22%, transparent), transparent 60%),
    radial-gradient(circle at 85% 80%, color-mix(in srgb, var(--bio-accent) 10%, transparent), transparent 40%);
  opacity: 0.9;
}

.link-bio-page[style*='background-image'] .link-bio-atmosphere {
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.35), rgba(15, 23, 42, 0.15));
}

.link-bio-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
  padding: 2.5rem 1.25rem 3rem;
}

@media (min-width: 640px) {
  .link-bio-shell {
    padding: 3.5rem 1.5rem 4rem;
  }
}

.link-bio-profile {
  text-align: center;
  margin-bottom: 2rem;
}

.link-bio-avatar-wrap {
  display: inline-block;
  margin-bottom: 1.25rem;
}

.link-bio-avatar {
  width: 7.5rem;
  height: 7.5rem;
  border-radius: 9999px;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.7);
}

@media (min-width: 640px) {
  .link-bio-avatar {
    width: 8.5rem;
    height: 8.5rem;
  }
}

.link-bio-avatar--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
}

.link-bio-name {
  font-family: var(--ps-font-display);
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 0.35rem;
}

.link-bio-role {
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.72;
  margin-bottom: 0.75rem;
}

.link-bio-bio {
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0.82;
  max-width: 22rem;
  margin: 0 auto;
}

.link-bio-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
}

.link-bio-btn {
  --btn-bg: #0f766e;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem 1.15rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  background: var(--btn-bg);
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: 0 10px 24px -16px color-mix(in srgb, var(--btn-bg) 80%, #000);
}

.link-bio-btn.is-rounded {
  border-radius: 0.85rem;
}

.link-bio-btn.is-square {
  border-radius: 0.15rem;
}

.link-bio-btn.is-pill {
  border-radius: 9999px;
}

.link-bio-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 28px -14px color-mix(in srgb, var(--btn-bg) 90%, #000);
}

.link-bio-btn:active {
  transform: translateY(0);
}

.link-bio-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
}

.link-bio-btn__label {
  flex: 1;
  text-align: left;
}

.link-bio-btn__arrow {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;
}

.link-bio-btn:hover .link-bio-btn__arrow,
.link-bio-btn:focus-visible .link-bio-btn__arrow {
  opacity: 1;
  transform: translateX(0);
}

.link-bio-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  border: 1px dashed;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(6px);
}

.link-bio-lead {
  margin-bottom: 1.5rem;
}

.link-bio-footer {
  text-align: center;
  margin-top: 2.5rem;
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.45;
  font-weight: 600;
}

/* Theme: glass */
.theme-glass .link-bio-btn {
  background: color-mix(in srgb, var(--btn-bg) 78%, transparent);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
}

.theme-glass .link-bio-avatar {
  border-color: rgba(255, 255, 255, 0.55);
}

.theme-glass .link-bio-atmosphere {
  backdrop-filter: blur(0px);
}

/* Theme: bold */
.theme-bold .link-bio-name {
  font-size: clamp(2rem, 5vw, 2.75rem);
  letter-spacing: -0.045em;
}

.theme-bold .link-bio-btn {
  padding: 1.15rem 1.25rem;
  font-size: 1rem;
  letter-spacing: -0.01em;
  box-shadow: 4px 4px 0 color-mix(in srgb, var(--bio-text) 35%, transparent);
}

.theme-bold .link-bio-btn:hover {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0 color-mix(in srgb, var(--bio-text) 35%, transparent);
}

.theme-bold .link-bio-role {
  font-size: 0.8rem;
}
</style>
